'use client';

import React from 'react';

import type { DropdownMenuProps } from '@radix-ui/react-dropdown-menu';

import { useEditorRef } from '@udecode/plate/react';
import { ArrowDownToLineIcon } from 'lucide-react';

// No external imports needed for basic serialization
import { downloadFile } from '@/lib/utils';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import JSZip from 'jszip';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  useOpenState,
} from './dropdown-menu';
import { ToolbarButton } from './toolbar';

export function ChapterExportToolbarButton({ children, ...props }: DropdownMenuProps) {
  const editor = useEditorRef();
  const openState = useOpenState();

  const getCanvas = async () => {
    const { default: html2canvas } = await import('html2canvas');

    const style = document.createElement('style');
    document.head.append(style);
    style.sheet?.insertRule(
      'body > div:last-child img { display: inline-block !important; }'
    );

    const canvas = await html2canvas(editor.api.toDOMNode(editor)!);
    style.remove();

    return canvas;
  };

  const exportToPdf = async () => {
    const canvas = await getCanvas();

    const PDFLib = await import('pdf-lib');
    const pdfDoc = await PDFLib.PDFDocument.create();
    const page = pdfDoc.addPage([canvas.width, canvas.height]);
    const imageEmbed = await pdfDoc.embedPng(canvas.toDataURL('PNG'));
    const { height, width } = imageEmbed.scale(1);
    page.drawImage(imageEmbed, {
      height,
      width,
      x: 0,
      y: 0,
    });
    const pdfBase64 = await pdfDoc.saveAsBase64({ dataUri: true });

    await downloadFile(pdfBase64, 'chapter.pdf');
  };

  const exportToImage = async () => {
    const canvas = await getCanvas();
    await downloadFile(canvas.toDataURL('image/png'), 'chapter.png');
  };

  // Simple text extraction function
  const extractText = (nodes: any[]): string => {
    return nodes.map(node => {
      if (node.children) {
        if (node.type === 'h1') return `# ${extractText(node.children)}\n\n`;
        if (node.type === 'h2') return `## ${extractText(node.children)}\n\n`;
        if (node.type === 'h3') return `### ${extractText(node.children)}\n\n`;
        if (node.type === 'blockquote') return `> ${extractText(node.children)}\n\n`;
        return extractText(node.children) + '\n\n';
      }
      return node.text || '';
    }).join('');
  };

  // Simple HTML conversion function
  const convertToHtml = (nodes: any[]): string => {
    return nodes.map(node => {
      if (node.children) {
        const text = convertToHtml(node.children);
        if (node.type === 'h1') return `<h1>${text}</h1>`;
        if (node.type === 'h2') return `<h2>${text}</h2>`;
        if (node.type === 'h3') return `<h3>${text}</h3>`;
        if (node.type === 'blockquote') return `<blockquote>${text}</blockquote>`;
        if (node.type === 'p') return `<p>${text}</p>`;
        return text;
      }
      return node.text || '';
    }).join('\n');
  };

  const exportChapterToHtml = async () => {
    try {
      const content = editor.children;
      const html = convertToHtml(content);

      const fullHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Chapter Export</title>
          <style>
            body {
              font-family: Georgia, serif;
              line-height: 1.6;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
            }
          </style>
        </head>
        <body>
          ${html}
        </body>
        </html>
      `;

      const url = `data:text/html;charset=utf-8,${encodeURIComponent(fullHtml)}`;
      await downloadFile(url, 'chapter.html');
    } catch (error) {
      console.error('Error exporting to HTML:', error);
    }
  };

  const exportChapterToMarkdown = async () => {
    try {
      const content = editor.children;
      const md = extractText(content);
      const url = `data:text/markdown;charset=utf-8,${encodeURIComponent(md)}`;
      await downloadFile(url, 'chapter.md');
    } catch (error) {
      console.error('Error exporting to Markdown:', error);
    }
  };

  const exportChapterToText = async () => {
    try {
      const content = editor.children;
      const text = content.map(block => {
        if (block.children) {
          return block.children.map((child: any) => child.text || '').join('');
        }
        return '';
      }).join('\n\n');
      const url = `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`;
      await downloadFile(url, 'chapter.txt');
    } catch (error) {
      console.error('Error exporting to Text:', error);
    }
  };

  // Convert editor content to DOCX paragraphs
  const convertToDocxParagraphs = (nodes: any[]): (Paragraph)[] => {
    const paragraphs: Paragraph[] = [];

    nodes.forEach(node => {
      if (node.children) {
        const text = node.children.map((child: any) => child.text || '').join('');

        if (node.type === 'h1') {
          paragraphs.push(
            new Paragraph({
              text: text,
              heading: HeadingLevel.HEADING_1,
              spacing: { after: 240, before: 480 },
            })
          );
        } else if (node.type === 'h2') {
          paragraphs.push(
            new Paragraph({
              text: text,
              heading: HeadingLevel.HEADING_2,
              spacing: { after: 200, before: 400 },
            })
          );
        } else if (node.type === 'h3') {
          paragraphs.push(
            new Paragraph({
              text: text,
              heading: HeadingLevel.HEADING_3,
              spacing: { after: 160, before: 320 },
            })
          );
        } else if (node.type === 'blockquote') {
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: text,
                  italics: true,
                }),
              ],
              indent: { left: 720 }, // 0.5 inch indent
              spacing: { after: 200 },
            })
          );
        } else {
          // Regular paragraph
          if (text.trim()) {
            paragraphs.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: text,
                  }),
                ],
                spacing: { after: 200 },
              })
            );
          }
        }
      }
    });

    return paragraphs;
  };

  const exportChapterToDocx = async () => {
    try {
      const content = editor.children;
      const paragraphs = convertToDocxParagraphs(content);

      // Create a new document
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: paragraphs,
          },
        ],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);

      await downloadFile(blob, 'chapter.docx');
    } catch (error) {
      console.error('Error exporting to DOCX:', error);
    }
  };

  const exportChapterToEpub = async () => {
    try {
      const content = editor.children;
      const html = convertToHtml(content);

      // Create EPUB structure
      const zip = new JSZip();

      // Add mimetype file
      zip.file('mimetype', 'application/epub+zip');

      // Add META-INF directory
      const metaInf = zip.folder('META-INF');
      metaInf?.file('container.xml', `<?xml version="1.0"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles>
    <rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/>
  </rootfiles>
</container>`);

      // Add OEBPS directory
      const oebps = zip.folder('OEBPS');

      // Add content.opf
      oebps?.file('content.opf', `<?xml version="1.0" encoding="UTF-8"?>
<package xmlns="http://www.idpf.org/2007/opf" unique-identifier="BookId" version="2.0">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:opf="http://www.idpf.org/2007/opf">
    <dc:title>Chapter Export</dc:title>
    <dc:language>en</dc:language>
    <dc:identifier id="BookId">chapter-export</dc:identifier>
    <dc:creator>Plot Studio</dc:creator>
  </metadata>
  <manifest>
    <item id="ncx" href="toc.ncx" media-type="application/x-dtbncx+xml"/>
    <item id="chapter" href="chapter.xhtml" media-type="application/xhtml+xml"/>
    <item id="stylesheet" href="styles.css" media-type="text/css"/>
  </manifest>
  <spine toc="ncx">
    <itemref idref="chapter"/>
  </spine>
</package>`);

      // Add toc.ncx
      oebps?.file('toc.ncx', `<?xml version="1.0" encoding="UTF-8"?>
<ncx xmlns="http://www.daisy.org/z3986/2005/ncx/" version="2005-1">
  <head>
    <meta name="dtb:uid" content="chapter-export"/>
    <meta name="dtb:depth" content="1"/>
    <meta name="dtb:totalPageCount" content="0"/>
    <meta name="dtb:maxPageNumber" content="0"/>
  </head>
  <docTitle>
    <text>Chapter Export</text>
  </docTitle>
  <navMap>
    <navPoint id="navpoint-1" playOrder="1">
      <navLabel>
        <text>Chapter</text>
      </navLabel>
      <content src="chapter.xhtml"/>
    </navPoint>
  </navMap>
</ncx>`);

      // Add styles.css
      oebps?.file('styles.css', `
body {
  font-family: Georgia, serif;
  line-height: 1.6;
  margin: 2em;
}
h1, h2, h3 {
  color: #333;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
}
p {
  margin-bottom: 1em;
  text-align: justify;
}
blockquote {
  margin: 1em 2em;
  font-style: italic;
  border-left: 3px solid #ccc;
  padding-left: 1em;
}
`);

      // Add chapter content
      oebps?.file('chapter.xhtml', `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.1//EN" "http://www.w3.org/TR/xhtml11/DTD/xhtml11.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
  <title>Chapter</title>
  <link rel="stylesheet" type="text/css" href="styles.css"/>
</head>
<body>
  ${html}
</body>
</html>`);

      // Generate EPUB file
      const epubBlob = await zip.generateAsync({ type: 'blob' });
      await downloadFile(epubBlob, 'chapter.epub');
    } catch (error) {
      console.error('Error exporting to EPUB:', error);
    }
  };

  const exportChapterToOdt = async () => {
    try {
      const content = editor.children;

      // Create ODT structure
      const zip = new JSZip();

      // Add mimetype
      zip.file('mimetype', 'application/vnd.oasis.opendocument.text');

      // Add META-INF directory
      const metaInf = zip.folder('META-INF');
      metaInf?.file('manifest.xml', `<?xml version="1.0" encoding="UTF-8"?>
<manifest:manifest xmlns:manifest="urn:oasis:names:tc:opendocument:xmlns:manifest:1.0" manifest:version="1.2">
  <manifest:file-entry manifest:full-path="/" manifest:version="1.2" manifest:media-type="application/vnd.oasis.opendocument.text"/>
  <manifest:file-entry manifest:full-path="content.xml" manifest:media-type="text/xml"/>
  <manifest:file-entry manifest:full-path="styles.xml" manifest:media-type="text/xml"/>
  <manifest:file-entry manifest:full-path="meta.xml" manifest:media-type="text/xml"/>
</manifest:manifest>`);

      const now = new Date();
      zip.file('meta.xml', `<?xml version="1.0" encoding="UTF-8"?>
<office:document-meta xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" xmlns:meta="urn:oasis:names:tc:opendocument:xmlns:meta:1.0" xmlns:dc="http://purl.org/dc/elements/1.1/" office:version="1.2">
  <office:meta>
    <meta:generator>Plot Studio</meta:generator>
    <meta:creation-date>${now.toISOString()}</meta:creation-date>
    <dc:title>Exported Chapter</dc:title>
    <dc:creator>Plot Studio</dc:creator>
    <dc:language>en-US</dc:language>
  </office:meta>
</office:document-meta>`);

      // Add styles.xml
      zip.file('styles.xml', `<?xml version="1.0" encoding="UTF-8"?>
<office:document-styles xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" xmlns:style="urn:oasis:names:tc:opendocument:xmlns:style:1.0" xmlns:fo="urn:oasis:names:tc:opendocument:xmlns:xsl-fo-compatible:1.0" office:version="1.2">
  <office:styles>
    <style:default-style style:family="paragraph">
      <style:paragraph-properties fo:text-align="justify"/>
      <style:text-properties style:font-name="Georgia" fo:font-size="12pt"/>
    </style:default-style>
    <style:style style:name="Heading_20_1" style:display-name="Heading 1" style:family="paragraph" style:parent-style-name="Heading" style:next-style-name="Text_20_body">
      <style:paragraph-properties fo:margin-top="0.423cm" fo:margin-bottom="0.212cm" fo:keep-with-next="always"/>
      <style:text-properties fo:font-size="18pt" fo:font-weight="bold"/>
    </style:style>
    <style:style style:name="Heading_20_2" style:display-name="Heading 2" style:family="paragraph" style:parent-style-name="Heading" style:next-style-name="Text_20_body">
      <style:paragraph-properties fo:margin-top="0.353cm" fo:margin-bottom="0.176cm" fo:keep-with-next="always"/>
      <style:text-properties fo:font-size="16pt" fo:font-weight="bold"/>
    </style:style>
    <style:style style:name="Heading_20_3" style:display-name="Heading 3" style:family="paragraph" style:parent-style-name="Heading" style:next-style-name="Text_20_body">
      <style:paragraph-properties fo:margin-top="0.247cm" fo:margin-bottom="0.176cm" fo:keep-with-next="always"/>
      <style:text-properties fo:font-size="14pt" fo:font-weight="bold"/>
    </style:style>
  </office:styles>
</office:document-styles>`);

      // Convert content to ODT format
      const convertToOdtContent = (nodes: any[]): string => {
        return nodes.map(node => {
          if (node.children) {
            const text = node.children.map((child: any) => child.text || '').join('');
            if (node.type === 'h1') {
              return `<text:h text:style-name="Heading_20_1" text:outline-level="1">${text}</text:h>`;
            } else if (node.type === 'h2') {
              return `<text:h text:style-name="Heading_20_2" text:outline-level="2">${text}</text:h>`;
            } else if (node.type === 'h3') {
              return `<text:h text:style-name="Heading_20_3" text:outline-level="3">${text}</text:h>`;
            } else if (node.type === 'blockquote') {
              return `<text:p><text:span text:style-name="Emphasis">${text}</text:span></text:p>`;
            } else if (text.trim()) {
              return `<text:p>${text}</text:p>`;
            }
          }
          return '';
        }).join('\n');
      };

      const odtContent = convertToOdtContent(content);

      // Add content.xml
      zip.file('content.xml', `<?xml version="1.0" encoding="UTF-8"?>
<office:document-content xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" xmlns:text="urn:oasis:names:tc:opendocument:xmlns:text:1.0" office:version="1.2">
  <office:body>
    <office:text>
      ${odtContent}
    </office:text>
  </office:body>
</office:document-content>`);

      // Generate ODT file
      const odtBlob = await zip.generateAsync({ type: 'blob' });
      await downloadFile(odtBlob, 'chapter.odt');
    } catch (error) {
      console.error('Error exporting to ODT:', error);
    }
  };

  return (
    <DropdownMenu modal={false} {...openState} {...props}>
      <DropdownMenuTrigger asChild>
        <ToolbarButton pressed={openState.open} tooltip="Export Chapter" isDropdown>
          <ArrowDownToLineIcon className="size-4" />
        </ToolbarButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start">
        <DropdownMenuGroup>
          <DropdownMenuItem onSelect={exportChapterToHtml}>
            Export as HTML
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={exportChapterToDocx}>
            Export as DOCX
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={exportChapterToOdt}>
            Export as ODT
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={exportChapterToEpub}>
            Export as EPUB
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={exportToPdf}>
            Export as PDF
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={exportToImage}>
            Export as Image
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={exportChapterToMarkdown}>
            Export as Markdown
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={exportChapterToText}>
            Export as Plain Text
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
